import { Field, Button } from "../components/rumonai";
import { useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { LightMotion } from "../components/LightMotion";
import { platformRequest, type PlatformCredential } from "./api";
import {
  CommandFeedback, PageHeading, PlatformEmpty, PlatformError, PlatformLoading, usePlatformCommand, usePlatformRead
} from "./shared";

/**
 * 渠道连接: the outside applications the platform registers once for every owner.
 *
 * The page knows no application by name. It draws what `/api/admin/credentials` lists (the title, the
 * note and the fields each plugin declared) and sends back only what the admin typed. A secret is never
 * read back: a field the Hub holds shows that it is set, and an empty box leaves it alone.
 */
export function PlatformConnections() {
  const reading = usePlatformRead<{ credentials: PlatformCredential[] }>("/api/admin/credentials");
  return <>
    <PageHeading title="渠道连接">
      <p>用户连接自己的账号时用的应用，在这里登记一次，云端的 Agent 都会带上。</p>
    </PageHeading>
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!reading.data && reading.loading ? <PlatformLoading label="正在读取渠道连接" /> : null}
    {reading.data ? reading.data.credentials.length ? (
      <div className="admin-connections">
        {reading.data.credentials.map(credential =>
          <ConnectionCard key={credential.key} credential={credential} onSaved={reading.reload} />)}
      </div>
    ) : (
      <PlatformEmpty title="没有需要登记的连接">
        <p>装上需要平台应用的渠道插件后，会在这里出现。</p>
      </PlatformEmpty>
    ) : null}
  </>;
}

function ConnectionCard({ credential, onSaved }: { credential: PlatformCredential; onSaved: () => void }) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [clearing, setClearing] = useState(false);
  const command = usePlatformCommand();
  const held = credential.fields.some(field => field.present);
  // What the admin typed that is not already held. An emptied box is not a change: it leaves the value alone.
  const changes = credential.fields.flatMap(field => {
    const text = (draft[field.key] ?? "").trim();
    return text && text !== (field.value ?? "") ? [[field.key, text] as const] : [];
  });
  const send = (body: { values?: Record<string, string>; clear?: string[]; reason: string }, notice: string) =>
    command.run(async () => {
      await platformRequest(`/api/admin/credentials/${encodeURIComponent(credential.key)}`, {
        method: "PUT", body: JSON.stringify(body),
      });
      setDraft({}); setClearing(false);
      command.setNotice(notice);
      onSaved();
    });
  const save = (event: FormEvent) => {
    event.preventDefault();
    if (!changes.length) return;
    void send({
      values: Object.fromEntries(changes),
      reason: `${credential.ready ? "更换" : "登记"}${credential.title}`,
    }, credential.ready ? "已更换，云端 Agent 马上带上新的。" : "已开通，云端 Agent 马上带上。");
  };
  const clear = () => void send({
    clear: credential.fields.filter(field => field.present).map(field => field.key),
    reason: `清除${credential.title}`,
  }, "已清除。");
  return <section className="admin-connection" aria-labelledby={`connection-${credential.key}`}>
    <header>
      <h2 id={`connection-${credential.key}`}>{credential.title}</h2>
      <span className={`platform-instance-status${credential.ready ? " is-online" : ""}`}>
        <i />{credential.ready ? "已开通" : "还没开通"}
      </span>
    </header>
    {credential.note ? <p className="admin-connection-note">{credential.note}</p> : null}
    <form className="platform-admin-form" noValidate onSubmit={save} aria-busy={command.pending}>
      <fieldset disabled={command.pending}>
        {credential.fields.map(field => <Field className="field" key={field.key} label={field.title}><input
            className="text-input"
            name={field.key}
            type={field.shown ? "text" : "password"}
            autoComplete="off"
            spellCheck={false}
            value={draft[field.key] ?? field.value ?? ""}
            placeholder={field.shown ? "" : field.present ? "已登记，不再显示；要换时才填" : ""}
            onChange={event => setDraft(old => ({ ...old, [field.key]: event.target.value }))}
          /></Field>)}
      </fieldset>
      <div className="admin-card-foot">
        <Button variant="primary" type="submit" disabled={command.pending || !changes.length}
          aria-busy={command.pending}>
          {command.pending ? <><LightMotion state="working" compact />正在保存</>
            : credential.ready ? "更换" : "保存并开通"}
        </Button>
        {held && !clearing ? <Button variant="ghost" type="button" disabled={command.pending}
          onClick={() => setClearing(true)}>清除登记</Button> : null}
        <CommandFeedback error={command.error} notice={command.notice}
          failed={clearing ? "没有清除登记" : "没有保存登记"} />
      </div>
      {credential.ready ? <small>换成另一个应用后，用户已经连上的账号要各自重新连接。</small> : null}
      {clearing ? <div className="admin-action-confirm pa-reveal" role="group" aria-label="确认清除">
        <p>清除后，用户不能再连接新的账号，已经连上的账号也不能再用，直到重新登记。</p>
        <div className="platform-actions">
          <Button variant="danger" type="button" disabled={command.pending} onClick={clear}>确定清除</Button>
          <Button variant="ghost" type="button" disabled={command.pending}
            onClick={() => setClearing(false)}>取消</Button>
        </div>
      </div> : null}
    </form>
    <div className="admin-connection-return">
      <h3>返回地址</h3>
      {credential.callbacks.length ? <>
        {credential.callback_note ? <p>{credential.callback_note}</p> : null}
        {credential.callbacks.map(address =>
          <input key={address} className="text-input" readOnly value={address} aria-label="返回地址"
            onFocus={event => event.currentTarget.select()} />)}
      </> : <p><Icon name="alert" size={16} />平台的网址还没设好，暂时给不出返回地址。</p>}
    </div>
  </section>;
}
